import { AlertCircle, RefreshCw } from 'lucide-react'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Alert, AlertDescription, AlertTitle } from './alert'

interface Props {
    children: ReactNode
    fallbackDescription?: string
    onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
    hasError: boolean
    error: Error | null
}

/**
 * Granular Error Boundary for isolated widgets/components.
 * Prevents the entire page from crashing if a single widget fails.
 */
export class WidgetErrorBoundary extends Component<Props, State> {
    public override state: State = {
        hasError: false,
        error: null,
    }

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error }
    }

    public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('[WidgetErrorBoundary] Caught error:', error, errorInfo)
        if (this.props.onError) {
            this.props.onError(error, errorInfo)
        }
    }

    private handleRetry = () => {
        this.setState({ hasError: false, error: null })
    }

    public override render() {
        if (this.state.hasError) {
            return (
                <div className="flex flex-col items-center justify-center p-6 min-h-[150px] animate-in fade-in zoom-in-95 duration-300">
                    <Alert
                        variant="destructive"
                        className="max-w-md bg-white dark:bg-slate-900 border-red-200 dark:border-red-900 shadow-sm"
                    >
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>{this.props.fallbackDescription || 'Widget Error'}</AlertTitle>
                        <AlertDescription className="mt-2 flex flex-col gap-3">
                            <p>An unexpected error occurred while rendering this component.</p>
                            <button
                                onClick={this.handleRetry}
                                className="self-start flex items-center gap-2 px-3 py-1.5 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 text-xs font-bold rounded-md transition-colors"
                            >
                                <RefreshCw size={12} />
                                Retry
                            </button>
                            {import.meta.env.DEV && this.state.error && (
                                <pre className="mt-2 p-2 bg-red-50 dark:bg-red-950/30 text-red-800 dark:text-red-200 text-[10px] rounded overflow-auto max-w-full text-left font-mono">
                                    {this.state.error.toString()}
                                </pre>
                            )}
                        </AlertDescription>
                    </Alert>
                </div>
            )
        }

        return this.props.children
    }
}
